import { matchPath } from "react-router-dom";

import { Routes } from "constants/routes";
import { IBookmark, IReportType, UrlQueryParams } from "store/types";

import { BookmarkPageDescriptor, CurrentPageContext } from "./types";

// Context passed to buildBookmarkSaveParams: everything needed to build the params
// string for bookmark.params for any of the supported types.
export interface BookmarkSaveContext {
  pathname: string;
  search: string;
  queryParams: UrlQueryParams;
  reportsQueries: unknown;
}

// Result of buildBookmarkSaveParams — the payload that goes into create/update bookmark.
export interface BookmarkSaveParams {
  params: string;
  reportType?: BookmarkReportType;
}

// The backend's bookmark.report_type field accepts only 4 of the 5 IReportType values
// (PORTFOLIO_OVERVIEW is the Dashboard mode and is not used in the bookmark flow).
type BookmarkReportType = IReportType.PORTFOLIO | IReportType.COMPARISON | IReportType.DEAL_VIEW | IReportType.EXTERNAL;

// Runtime Set for narrowing IReportType → BookmarkReportType (see asBookmarkReportType).
const BOOKMARK_REPORT_TYPES: ReadonlySet<IReportType> = new Set([
  IReportType.PORTFOLIO,
  IReportType.COMPARISON,
  IReportType.DEAL_VIEW,
  IReportType.EXTERNAL
]);

// "Page" descriptors used to group and label bookmarks in the Saved Views panel.
// `key` is a stable group identifier; `label` is what the user sees.
const PAGE_QUICK_VIEW: BookmarkPageDescriptor = { key: "quick_view", label: "Smart Sheets" };
const PAGE_MY_DEALS: BookmarkPageDescriptor = { key: "my_deals", label: "My Deals" };
const PAGE_INVESTOR_CORRELATION: BookmarkPageDescriptor = { key: "correlation_matrix", label: "Investor Correlation" };
const PAGE_MONITORING: BookmarkPageDescriptor = { key: "data_operations", label: "Monitoring" };
const PAGE_PORTFOLIO_ANALYSIS: BookmarkPageDescriptor = { key: "report_portfolio", label: "Portfolio Analysis" };
const PAGE_COMPARISON: BookmarkPageDescriptor = { key: "report_comparison", label: "Comparison" };
const PAGE_DEAL_REPORT: BookmarkPageDescriptor = { key: "report_deal_view", label: "Deal Report" };
const PAGE_EXTERNAL_REPORT: BookmarkPageDescriptor = { key: "report_external", label: "External Report" };

// A report bookmark stores its `report_type` as a separate field — this map translates it
// to the matching PAGE_*, so report bookmarks land under the right sub-section.
const REPORT_PAGE_BY_TYPE: Partial<Record<IReportType, BookmarkPageDescriptor>> = {
  [IReportType.PORTFOLIO]: PAGE_PORTFOLIO_ANALYSIS,
  [IReportType.COMPARISON]: PAGE_COMPARISON,
  [IReportType.DEAL_VIEW]: PAGE_DEAL_REPORT,
  [IReportType.EXTERNAL]: PAGE_EXTERNAL_REPORT
};

// Bookmark object → its page descriptor.
// Used by the Saved Views panel to group the owned-bookmarks list by section.
export const getBookmarkPage = (bookmark: IBookmark): BookmarkPageDescriptor => {
  switch (bookmark.type) {
    case "quick_view":
      return PAGE_QUICK_VIEW;
    case "deal":
    case "investor_view":
      return PAGE_MY_DEALS;
    case "correlation_matrix":
      return PAGE_INVESTOR_CORRELATION;
    case "data_operations":
      return PAGE_MONITORING;
    case "report":
      return (bookmark.report_type && REPORT_PAGE_BY_TYPE[bookmark.report_type]) || PAGE_PORTFOLIO_ANALYSIS;
    default:
      return PAGE_PORTFOLIO_ANALYSIS;
  }
};

// List of actual URL routes treated as report pages, and which IReportType they map to.
// Needed to determine the report type from the current pathname
// (e.g. saving from /timeline → IReportType.PORTFOLIO).
const REPORT_ROUTE_TO_TYPE: Array<{ route: string; reportType: IReportType }> = [
  { route: Routes.snapshot, reportType: IReportType.PORTFOLIO },
  { route: Routes.timeline, reportType: IReportType.PORTFOLIO },
  { route: Routes.classic, reportType: IReportType.PORTFOLIO },
  { route: Routes.cashflow, reportType: IReportType.PORTFOLIO },
  { route: Routes.comparison, reportType: IReportType.COMPARISON },
  { route: Routes.dealReport, reportType: IReportType.DEAL_VIEW }
];

/**
 * For the current pathname returns the bookmark-page context, or null if bookmarks
 * are not supported on this route.
 *
 * Consumers:
 *  - Bookmark.tsx — show the "Save view of <pageLabel>" button only on supported pages.
 *  - BookmarkPanel — highlight the group matching the current page (isCurrent).
 *  - ViewRow — decide whether the "Save current view" menu item is enabled
 *    (only when the bookmark's type matches the current page's type).
 *  - SaveViewModal / ViewRowMenu — build params via buildBookmarkSaveParams.
 *
 * `target` is the key in UrlQueryParams under which the page's JSON state is stored
 * (used by every type except `report` — for reports the state lives in Redux, not the URL).
 */
export const getCurrentPageContext = (pathname: string): CurrentPageContext | null => {
  if (matchPath(pathname, Routes.quickView)) {
    return {
      pageKey: PAGE_QUICK_VIEW.key,
      pageLabel: PAGE_QUICK_VIEW.label,
      target: "quickViewQueries",
      type: "quick_view"
    };
  }
  if (matchPath(pathname, Routes.myDeals)) {
    return { pageKey: PAGE_MY_DEALS.key, pageLabel: PAGE_MY_DEALS.label, target: "deals", type: "deal" };
  }
  if (matchPath(pathname, Routes.portfolioConstructionInvestorCorrelation)) {
    return {
      pageKey: PAGE_INVESTOR_CORRELATION.key,
      pageLabel: PAGE_INVESTOR_CORRELATION.label,
      target: "correlationMatrixSettings",
      type: "correlation_matrix"
    };
  }
  if (matchPath(pathname, Routes.monitoring)) {
    return {
      pageKey: PAGE_MONITORING.key,
      pageLabel: PAGE_MONITORING.label,
      target: "dataOperations",
      type: "data_operations"
    };
  }
  // Report routes share a single lookup: the type is resolved via REPORT_ROUTE_TO_TYPE.
  const matchedReport = REPORT_ROUTE_TO_TYPE.find(({ route }) => matchPath(pathname, route));
  if (matchedReport) {
    const page = REPORT_PAGE_BY_TYPE[matchedReport.reportType] ?? PAGE_PORTFOLIO_ANALYSIS;
    return { pageKey: page.key, pageLabel: page.label, target: "reports", type: "report" };
  }
  return null;
};

// For report routes returns the matching IReportType.
// Used by buildBookmarkSaveParams to write report_type onto the bookmark.
export const getCurrentReportType = (pathname: string): IReportType | undefined => {
  const matched = REPORT_ROUTE_TO_TYPE.find(({ route }) => matchPath(pathname, route));
  return matched?.reportType;
};

// Narrows IReportType to the values the bookmark API accepts.
// Anything else (e.g. PORTFOLIO_OVERVIEW) → undefined.
const asBookmarkReportType = (t: IReportType | undefined): BookmarkReportType | undefined =>
  t && BOOKMARK_REPORT_TYPES.has(t) ? (t as BookmarkReportType) : undefined;

/**
 * Builds the string that goes into bookmark.params when creating a new bookmark or
 * overwriting an existing one. The single point of params construction, used by:
 *  - SaveViewModal.handleSubmit  (create)
 *  - ViewRowMenu.handleOverrideBookmark  ("Save current view" overwrite)
 *
 * Logic differs by page type:
 *  - data_operations — page state (Monitoring/Consolidation/FileRepo filters) lives
 *    ENTIRELY in the URL search; it is not in Redux. So params = pathname + search "as is".
 *  - report — state lives in Redux (state.reports.queries), not in the URL.
 *    Serialize reportsQueries to JSON and put it under ?reports=<json>.
 *  - everything else (quick_view / deal / investor_view / correlation_matrix) — state
 *    is in the URL under a single key (queryParams[target]). Take its serialized JSON
 *    as is and place it under ?<target>=<encoded>.
 *
 * Returns null when a supported page has empty state — the caller surfaces an api-error.
 */
export const buildBookmarkSaveParams = (
  page: CurrentPageContext,
  ctx: BookmarkSaveContext
): BookmarkSaveParams | null => {
  const { pathname, search, queryParams, reportsQueries } = ctx;

  if (page.type === "data_operations") {
    return { params: pathname + (search || "") };
  }

  let resultQuery = "";
  if (page.type === "report") {
    resultQuery = JSON.stringify(reportsQueries);
  } else {
    resultQuery = (queryParams[page.target as keyof UrlQueryParams] as string) || "";
  }
  if (!resultQuery) return null;

  return {
    params: pathname + `?${page.target}=` + encodeURIComponent(resultQuery),
    reportType: page.type === "report" ? asBookmarkReportType(getCurrentReportType(pathname)) : undefined
  };
};

// Backend role IDs allowed in bookmark.shared_with. There is no shared FE constant for
export const AVAILABLE_ROLES_FOR_BOOKMARK = [1, 4, 6];

// Fallback initials for avatars (PeoplePicker / SharedRow / ShareIcon):
export const getInitials = (fullName: string | undefined | null): string => {
  if (!fullName) return "";
  return fullName
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
};
